import React from "react";
import styled from "styled-components";
import { FiTrash2, FiVideo, FiStar } from "react-icons/fi";
import { FaStar } from "react-icons/fa";
import { theme } from "../../styles/theme";
import { Button } from "../ui/Button";
import { StatusBadge } from "../ui/StatusBadge";

const PanelWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
`;

const SeedCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  flex: 1 1 clamp(220px, 40vw, 320px);
  max-width: 340px;
  padding: ${theme.spacing.sm};
  border: ${theme.borders.base};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.secondary};
  align-items: center;
`;

const Preview = styled.div`
  width: 100%;
  aspect-ratio: 9 / 16;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background: ${theme.colors.background.tertiary};
  flex-shrink: 0;
  border: ${theme.borders.base};
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  background: ${theme.colors.background.tertiary};
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const SeedInfo = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing["2xs"]};
`;

const SeedMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing["3xs"]};
  align-items: flex-start;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  width: 100%;
  align-items: stretch;
`;

const FilterTabs = styled.div`
  width: 100%;
  display: flex;
  gap: ${theme.spacing.xs};
`;

const FilterTabButton = styled.button`
  border: ${theme.borders.base};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing["2xs"]} ${theme.spacing.sm};
  background: ${({ active }) =>
    active ? theme.colors.background.quaternary : theme.colors.background.secondary};
  color: ${({ active }) =>
    active ? theme.colors.text.primary : theme.colors.text.secondary};
  cursor: pointer;
  font-size: ${theme.typography.fontSize.sm};
`;

function SeedsPanel({ seeds, onCreateProject, onDeleteSeed, onToggleStar, busySeedId }) {
  const [activeFilter, setActiveFilter] = React.useState("all");

  const sortedSeeds = React.useMemo(() => {
    return [...seeds].sort((a, b) => {
      const aTime = Number(a.created_at || 0);
      const bTime = Number(b.created_at || 0);
      return bTime - aTime;
    });
  }, [seeds]);

  const visibleSeeds = React.useMemo(() => {
    if (activeFilter === "favourites") {
      return sortedSeeds.filter((seed) => Boolean(seed.starred));
    }
    return sortedSeeds;
  }, [activeFilter, sortedSeeds]);

  if (!sortedSeeds.length) {
    return <span style={{ color: theme.colors.text.muted }}>No seeds yet.</span>;
  }

  return (
    <>
      <FilterTabs>
        <FilterTabButton
          type="button"
          active={activeFilter === "all"}
          onClick={() => setActiveFilter("all")}
        >
          All
        </FilterTabButton>
        <FilterTabButton
          type="button"
          active={activeFilter === "favourites"}
          onClick={() => setActiveFilter("favourites")}
        >
          Favourites
        </FilterTabButton>
      </FilterTabs>

      {!visibleSeeds.length ? (
        <span style={{ color: theme.colors.text.muted }}>No favourite seeds yet.</span>
      ) : (
        <PanelWrapper>
          {visibleSeeds.map((seed) => (
        <SeedCard key={seed.id}>
          <SeedInfo>
            <SeedMeta>
              <StatusBadge status={seed.status || "pending"}>
                {seed.status || "pending"}
              </StatusBadge>
              <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                Duration: {(seed.duration_seconds || 0).toFixed(1)}s
              </span>
            </SeedMeta>
            <Button
              size="small"
              variant="ghost"
              onClick={() => onToggleStar?.(seed)}
              style={{ minWidth: 0, padding: `${theme.spacing["3xs"]} ${theme.spacing["2xs"]}` }}
            >
              {seed.starred ? <FaStar color="#f5c518" /> : <FiStar />}
            </Button>
          </SeedInfo>

          <Preview>
            {seed.video_url ? (
              <PreviewVideo
                src={seed.video_url}
                poster={seed.preview_image_url || undefined}
                controls
                muted
                playsInline
                preload="metadata"
              />
            ) : seed.preview_image_url ? (
              <PreviewImage src={seed.preview_image_url} alt="Seed preview" />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "180px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.colors.text.muted,
                  fontSize: theme.typography.fontSize.xs,
                }}
              >
                Pending
              </div>
            )}
          </Preview>

          <Actions>
            <Button
              size="small"
              fullWidth
              onClick={() => onCreateProject(seed)}
              disabled={busySeedId === seed.id || seed.status !== "ready"}
            >
              {seed.status !== "ready"
                ? "Processing…"
                : busySeedId === seed.id
                ? "Preparing..."
                : "Create project"}
            </Button>
            <Button
              size="small"
              variant="secondary"
              fullWidth
              onClick={() => onDeleteSeed(seed.id)}
              disabled={busySeedId === seed.id}
            >
              <FiTrash2 /> Delete
            </Button>
            <Button
              size="small"
              variant="ghost"
              fullWidth
              onClick={() =>
                window.open(seed.source_url || seed.video_url, "_blank", "noreferrer")
              }
            >
              <FiVideo /> View TikTok
            </Button>
          </Actions>
        </SeedCard>
          ))}
        </PanelWrapper>
      )}
    </>
  );
}

export default SeedsPanel;
